import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Heart, HelpingHand, Home as HomeIcon, Users, Building2, FileText,
  Upload, Download, AlertTriangle, ArrowRight, ShieldCheck, Handshake,
  TrendingUp, Award, BookOpen, Mail,
} from "lucide-react";
import { Section, Eyebrow, H2, Lead, GlassCard, Reveal } from "../components/site/Primitives";
import api from "../lib/api";
import { log } from "../lib/logger";

const PILLARS = [
  { icon: <HomeIcon />, title: "Veteran Housing", body: "Dignified, service-rich housing paths — transitional, recovery, affordable, and workforce — for those who served." },
  { icon: <Heart />, title: "Recovery & Transitional", body: "Sober-living, behavioral health, and case management partnerships with VA and regional clinical providers." },
  { icon: <HelpingHand />, title: "Workforce Reintegration", body: "Job placement, apprenticeships, and trade certifications connecting residents to living-wage careers." },
  { icon: <AlertTriangle />, title: "Emergency Assistance", body: "Rapid-response support for veterans and families facing immediate housing instability or crisis." },
  { icon: <Building2 />, title: "Community Redevelopment", body: "Neighborhood revitalization, mixed-use district transformation, and public-private partnership stewardship." },
  { icon: <BookOpen />, title: "Education & Wellness", body: "On-site programming — financial literacy, recovery groups, parenting, fitness, and family wraparound services." },
];

const BOARD_PLACEHOLDERS = [
  { name: "Founder & Chair", role: "Army Veteran · 220 Inc.", note: "Founder seat" },
  { name: "Vice Chair (Open)", role: "VA Partnerships", note: "Recruiting" },
  { name: "Treasurer (Open)", role: "Finance & Audit", note: "Recruiting" },
  { name: "Secretary (Open)", role: "Governance & Compliance", note: "Recruiting" },
  { name: "Director (Open)", role: "Community & Outreach", note: "Recruiting" },
  { name: "Director (Open)", role: "Workforce Development", note: "Recruiting" },
];

const PARTNERS = [
  "VA Medical Centers", "Veteran-Serving Nonprofits", "Behavioral Health Providers",
  "Workforce Boards & Trade Unions", "County Redevelopment Agencies", "Faith-Based Community Partners",
  "Continuum of Care (CoC)", "Public Housing Authorities", "Foundations & Family Offices",
];

const GRANT_READINESS = [
  { label: "Mission alignment statement", status: "Drafted" },
  { label: "501(c)(3) determination", status: "In Formation" },
  { label: "Board of Directors", status: "Recruiting" },
  { label: "Conflict of interest policy", status: "Drafted" },
  { label: "Annual budget & financials", status: "In Progress" },
  { label: "Audit & form 990 readiness", status: "Planned" },
  { label: "Impact measurement framework", status: "Drafted" },
  { label: "Grant pipeline (federal/state/private)", status: "Building" },
];

export default function Foundation() {
  const [tab, setTab] = useState("donate");

  return (
    <div data-testid="foundation-page">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[85vh] flex items-end pt-40 pb-24 grain overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=2200&q=80"
            alt=""
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-[#0A0A0A]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-transparent to-transparent" />
        </div>
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-gold/10 rounded-full blur-[180px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-white/5 rounded-full blur-[160px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-gold mb-4">
            Nonprofit Arm · Separate Entity
          </div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold border border-gold/30 px-3 py-2 bg-black/40 mb-8">
            <ShieldCheck size={14} /> 220 Community Foundation
          </div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95] max-w-5xl">
            Housing is the <span className="text-gold">foundation</span>.
            <br />Community is the mission.
          </h1>
          <p className="mt-8 text-lg sm:text-2xl text-white/85 max-w-3xl leading-relaxed font-light">
            A community-focused nonprofit supporting veteran housing, recovery, workforce reintegration,
            emergency assistance, and neighborhood redevelopment — working alongside 220 Inc. but
            governed as a separate mission-driven entity.
          </p>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-3xl">
            {[
              { k: "6", l: "Mission Pillars" },
              { k: "100%", l: "Mission-Restricted" },
              { k: "0%", l: "Diverted to For-Profit" },
              { k: "9+", l: "Partner Categories" },
            ].map((s) => (
              <div key={s.l} className="bg-black/60 backdrop-blur-sm p-4 sm:p-5">
                <div className="font-display text-2xl sm:text-3xl text-gold">{s.k}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/55 mt-1">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#tabs" className="btn-primary text-sm" data-testid="foundation-cta-donate">
              Donate to the Foundation <Heart size={14} />
            </a>
            <a href="#tabs" className="btn-ghost text-sm" data-testid="foundation-cta-volunteer">
              Volunteer <HelpingHand size={14} />
            </a>
            <a href="#tabs" className="btn-ghost text-sm" data-testid="foundation-cta-partner">
              Partner With Us <Handshake size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ============ STRUCTURE / DISCLAIMER STRIP ============ */}
      <section className="border-y border-gold/20 bg-gradient-to-r from-black via-[#13110a] to-black" data-testid="foundation-disclaimer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-2 flex items-center gap-2 text-gold text-xs uppercase tracking-[0.3em]">
            <FileText size={14} /> Disclosure
          </div>
          <p className="md:col-span-10 text-sm sm:text-base text-white/75 leading-relaxed">
            220 Community Foundation is intended to operate as a <span className="text-white">separate nonprofit entity</span> supporting
            housing, recovery, workforce, and community redevelopment initiatives. While it works in close alignment
            with 220 Inc., its governance, finances, and grant-making activities are independent and mission-restricted.
          </p>
        </div>
      </section>

      {/* ============ MISSION ============ */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal>
            <div className="lg:col-span-7">
              <Eyebrow>Our Mission</Eyebrow>
              <H2 className="max-w-3xl">
                Restore the path home — for veterans, for families, for the community.
              </H2>
              <p className="mt-8 text-white/75 leading-relaxed text-lg max-w-3xl">
                We exist to make housing stability, recovery support, and economic dignity reachable for
                those who served and for the communities that surround them. The Foundation funds and
                operates programs alongside 220 Inc. infrastructure — turning real estate into a platform
                for human outcomes.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/veteran-housing" className="btn-ghost text-sm" data-testid="foundation-link-vhi">
                  See the Veteran Housing Initiative <ArrowRight size={14} />
                </Link>
                <Link to="/about" className="btn-ghost text-sm" data-testid="foundation-link-about">
                  About 220 Inc.
                </Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="lg:col-span-5">
              <GlassCard className="p-8">
                <div className="font-display text-2xl mb-6">For-Profit vs. Nonprofit</div>
                <div className="space-y-5 text-sm">
                  <div className="border-l-2 border-gold pl-4">
                    <div className="text-gold text-[11px] uppercase tracking-[0.25em] mb-1">220 Inc. — For-Profit</div>
                    <div className="text-white/80">Real estate development, construction, hospitality, and operating businesses that fund and sustain the platform.</div>
                  </div>
                  <div className="border-l-2 border-white/40 pl-4">
                    <div className="text-white/60 text-[11px] uppercase tracking-[0.25em] mb-1">220 Community Foundation — Nonprofit</div>
                    <div className="text-white/80">Programs, services, donor funds, and grants directly serving veterans and the community.</div>
                  </div>
                  <div className="pt-3 border-t border-white/10 text-xs text-white/55">
                    Both entities work in tandem toward the same mission, but maintain independent governance, funds, and reporting.
                  </div>
                </div>
              </GlassCard>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* ============ PILLARS ============ */}
      <Section className="border-t border-white/5">
        <Eyebrow>What We Fund</Eyebrow>
        <H2 className="max-w-3xl">Six pillars. One integrated path home.</H2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PILLARS.map((p) => (
            <GlassCard key={p.title} className="p-7" data-testid={`foundation-pillar-${p.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}>
              <div className="text-gold mb-4">{p.icon}</div>
              <div className="font-display text-xl mb-2">{p.title}</div>
              <p className="text-sm text-white/65 leading-relaxed">{p.body}</p>
            </GlassCard>
          ))}
        </div>
      </Section>

      {/* ============ IMPACT DASHBOARD ============ */}
      <Section className="border-t border-white/5">
        <Eyebrow>Impact Snapshot</Eyebrow>
        <H2 className="max-w-3xl">Outcomes we measure — and report.</H2>
        <Lead className="mt-4">
          Targets for Year One of foundation operations. Live dashboards will be published quarterly
          alongside audited financials.
        </Lead>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="foundation-impact-grid">
          {[
            { k: "540+", l: "Veteran-Priority Units (Pipeline)" },
            { k: "200", l: "Targeted Placements / Year" },
            { k: "12", l: "Wraparound Services On-Site" },
            { k: "$2.4M", l: "Year-1 Program Budget Target" },
            { k: "30%", l: "Recovery Housing Allocation" },
            { k: "100%", l: "Mission-Restricted Funds" },
            { k: "<60d", l: "Emergency Response Window" },
            { k: "Q1 '27", l: "First Impact Report" },
          ].map((s) => (
            <div key={s.l} className="bg-[#0c0c0e] p-5 sm:p-6">
              <div className="font-display text-2xl sm:text-3xl text-gold">{s.k}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/55 mt-2 leading-tight">{s.l}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ FORMS TABS ============ */}
      <Section id="tabs" className="border-t border-white/5">
        <Eyebrow>Take Action</Eyebrow>
        <H2 className="max-w-3xl">Give, serve, request help, or partner with us.</H2>
        <div className="mt-10 flex flex-wrap gap-2 border-b border-white/10 mb-10">
          {[
            { k: "donate", label: "Donate", icon: <Heart size={14} /> },
            { k: "volunteer", label: "Volunteer", icon: <HelpingHand size={14} /> },
            { k: "partner", label: "Partner Inquiry", icon: <Handshake size={14} /> },
            { k: "assistance", label: "Resident Assistance", icon: <HomeIcon size={14} /> },
            { k: "grant", label: "Grant Documents", icon: <Upload size={14} /> },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-gold text-gold" : "border-transparent text-white/60 hover:text-white"
              }`}
              data-testid={`foundation-tab-${t.k}`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === "donate" && <FoundationDonateForm />}
        {tab === "volunteer" && <FoundationVolunteerForm />}
        {tab === "partner" && <FoundationPartnerForm />}
        {tab === "assistance" && <FoundationAssistanceForm />}
        {tab === "grant" && <FoundationGrantUpload />}
      </Section>

      {/* ============ BOARD ============ */}
      <Section className="border-t border-white/5">
        <Eyebrow>Governance</Eyebrow>
        <H2 className="max-w-3xl">Board & Leadership.</H2>
        <Lead className="mt-4">
          The Foundation is in formation. Board seats are actively recruiting — strong preference for veterans,
          clinicians, housing/finance professionals, and community leaders rooted in the Columbus / Phenix City region.
        </Lead>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="foundation-board">
          {BOARD_PLACEHOLDERS.map((b) => (
            <div key={b.name} className="border border-white/10 bg-[#0c0c0e] p-6 hover:border-gold/40 transition-colors">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold/30 to-white/10 mb-4 flex items-center justify-center text-gold">
                <Users size={20} />
              </div>
              <div className="font-display text-lg">{b.name}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 mt-1">{b.role}</div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-gold/80">{b.note}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ PARTNERSHIPS ============ */}
      <Section className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal>
            <Eyebrow>Community Partnerships</Eyebrow>
            <H2>We co-design with the people closest to the mission.</H2>
            <p className="mt-6 text-white/70 leading-relaxed">
              The Foundation builds programs in partnership with VA medical centers, regional clinical
              providers, faith communities, workforce boards, and municipal redevelopment teams.
              Public-private collaboration is the operating model.
            </p>
            <button
              onClick={() => setTab("partner")}
              className="mt-8 btn-ghost text-sm"
              data-testid="foundation-partner-jump"
            >
              <a href="#tabs" className="inline-flex items-center gap-2">
                Submit a Partner Inquiry <ArrowRight size={14} />
              </a>
            </button>
          </Reveal>
          <Reveal delay={0.1}>
            <GlassCard className="p-8">
              <div className="font-display text-2xl mb-6">Partner Categories</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PARTNERS.map((p) => (
                  <div key={p} className="flex items-center gap-2 text-sm text-white/75">
                    <span className="w-1 h-1 bg-gold rounded-full" /> {p}
                  </div>
                ))}
              </div>
            </GlassCard>
          </Reveal>
        </div>
      </Section>

      {/* ============ GRANT & PUBLIC FUNDING READINESS ============ */}
      <Section className="border-t border-white/5">
        <Eyebrow>Grant & Public Funding Readiness</Eyebrow>
        <H2 className="max-w-3xl">Foundation infrastructure — built for institutional capital.</H2>
        <Lead className="mt-4">
          We are preparing the Foundation to qualify for federal, state, and private grant pipelines from day one.
        </Lead>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10" data-testid="foundation-grant-readiness">
          {GRANT_READINESS.map((g) => (
            <div key={g.label} className="bg-[#0c0c0e] p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-sm text-white/85">
                <Award size={16} className="text-gold flex-shrink-0" />
                {g.label}
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-gold/90 border border-gold/30 px-2 py-1 whitespace-nowrap">
                {g.status}
              </span>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ IMPACT REPORTS + DOWNLOAD ============ */}
      <Section className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal>
            <div className="lg:col-span-7">
              <Eyebrow>Impact Reports</Eyebrow>
              <H2>Transparent, donor-grade reporting.</H2>
              <p className="mt-6 text-white/70 leading-relaxed">
                We publish annual impact reports covering housing placements, recovery outcomes, workforce
                graduations, community-redevelopment milestones, and a full breakdown of how every dollar
                was deployed. Quarterly updates are issued to active donors and grantors.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-white/75">
                <li className="flex items-center gap-3"><TrendingUp size={14} className="text-gold" /> Annual Impact Report (Q1)</li>
                <li className="flex items-center gap-3"><FileText size={14} className="text-gold" /> Form 990 (when filed)</li>
                <li className="flex items-center gap-3"><Award size={14} className="text-gold" /> Independent Financial Audit</li>
                <li className="flex items-center gap-3"><Users size={14} className="text-gold" /> Quarterly Donor Briefings</li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="lg:col-span-5">
              <GlassCard className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold/30 to-white/10 mx-auto flex items-center justify-center text-gold mb-6">
                  <Download size={26} />
                </div>
                <div className="font-display text-2xl mb-3">Nonprofit Overview Packet</div>
                <p className="text-sm text-white/65 mb-6">
                  A 12-page brief covering mission, programs, governance, budget, and grant readiness.
                </p>
                <button
                  className="btn-primary text-sm w-full"
                  onClick={() => toast.info("Overview packet coming soon — leave your email via the Partner tab to receive it on release.")}
                  data-testid="foundation-download-packet"
                >
                  Download Overview Packet <Download size={14} />
                </button>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-4">
                  PDF · ~2.4 MB · Updated Quarterly
                </div>
              </GlassCard>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* ============ CONTACT STRIP ============ */}
      <Section className="border-t border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <Reveal>
            <Eyebrow>Foundation Contact</Eyebrow>
            <H2>Reach the Foundation team.</H2>
            <p className="mt-6 text-white/70 leading-relaxed">
              For donor questions, partnership coordination, grant submissions, or media inquiries
              specific to the Foundation, contact us directly.
            </p>
            <ul className="mt-8 space-y-4 text-sm text-white/85">
              <li className="flex items-center gap-3"><Mail size={16} className="text-gold" /> Travis.McGruder@220inc.com</li>
              <li className="flex items-center gap-3"><HomeIcon size={16} className="text-gold" /> Columbus, GA · Phenix City, AL</li>
            </ul>
            <div className="mt-4 text-[11px] uppercase tracking-[0.25em] text-white/50">
              Travis R. McGruder · Founder &amp; CEO, 220 Inc.
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <GlassCard className="p-8">
              <div className="font-display text-2xl mb-3">In Crisis?</div>
              <p className="text-sm text-white/70 leading-relaxed">
                If you are a veteran in immediate distress, call <span className="text-white">988 then press 1</span> for
                the Veterans Crisis Line. For housing emergencies, submit a Resident Assistance request
                above and a counselor will respond within hours.
              </p>
              <button
                onClick={() => setTab("assistance")}
                className="mt-6 btn-ghost text-sm"
                data-testid="foundation-crisis-jump"
              >
                <a href="#tabs" className="inline-flex items-center gap-2">
                  Submit a Resident Assistance Request <AlertTriangle size={14} />
                </a>
              </button>
            </GlassCard>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}

// ============================================================================
// Shared form primitives (kept local to avoid touching other pages)
// ============================================================================
function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <input className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" {...props} />
    </label>
  );
}
function Select({ label, options, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <select className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" {...props}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
function TextArea({ label, ...props }) {
  return (
    <label className="block md:col-span-2">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <textarea rows={4} className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" {...props} />
    </label>
  );
}

// ============================================================================
// DONATE
// ============================================================================
function FoundationDonateForm() {
  const [packages, setPackages] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [f, setF] = useState({
    pkg: "vh_100", custom: "", name: "", email: "",
    frequency: "one_time", designation: "community", message: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    api.get("/payments/packages").then((r) => {
      setPackages(r.data.donation_packages || []);
      setDesignations(r.data.designations || []);
    }).catch((e) => log.warn("foundation payment packages fetch failed", e?.message));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/payments/donations/checkout", {
        package_id: f.pkg === "custom" ? null : f.pkg,
        custom_amount: f.pkg === "custom" ? Number(f.custom) : null,
        donor_name: f.name,
        donor_email: f.email,
        frequency: f.frequency,
        designation: f.designation,
        message: f.message || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="foundation-form-donate">
      <div className="md:col-span-2">
        <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-3">Select Amount</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {packages.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => set("pkg", p.id)}
              className={`p-3 text-sm border transition-colors ${
                f.pkg === p.id ? "border-gold text-gold bg-gold/5" : "border-white/10 text-white/70 hover:border-white/30"
              }`}
              data-testid={`foundation-donate-pkg-${p.id}`}
            >
              <div className="font-display text-lg">${p.amount}</div>
            </button>
          ))}
          <button
            type="button"
            onClick={() => set("pkg", "custom")}
            className={`p-3 text-sm border transition-colors ${
              f.pkg === "custom" ? "border-gold text-gold bg-gold/5" : "border-white/10 text-white/70 hover:border-white/30"
            }`}
            data-testid="foundation-donate-pkg-custom"
          >
            <div className="font-display text-base">Custom</div>
          </button>
        </div>
        {f.pkg === "custom" && (
          <input
            type="number" min="1" placeholder="Enter amount (USD)"
            value={f.custom}
            onChange={(e) => set("custom", e.target.value)}
            className="mt-3 w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold"
            data-testid="foundation-donate-custom-amount"
          />
        )}
      </div>
      <Field label="Name *" required value={f.name} onChange={(e) => set("name", e.target.value)} />
      <Field label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
      <Select label="Frequency" value={f.frequency} onChange={(e) => set("frequency", e.target.value)} options={[
        { v: "one_time", l: "One-time" }, { v: "monthly", l: "Monthly" }, { v: "annual", l: "Annual" },
      ]} />
      <Select label="Designation" value={f.designation} onChange={(e) => set("designation", e.target.value)}
        options={designations.length ? designations.map((d) => ({ v: d.id, l: d.label })) : [{ v: "community", l: "Community Redevelopment" }]} />
      <TextArea label="Message (optional)" value={f.message} onChange={(e) => set("message", e.target.value)} />
      <div className="md:col-span-2">
        <button className="btn-primary" type="submit" data-testid="foundation-donate-submit">
          Continue to Secure Checkout <Heart size={14} />
        </button>
        <div className="text-xs text-white/40 mt-3">Powered by Stripe · Mission-restricted to your selected designation. Receipts emailed automatically.</div>
      </div>
    </form>
  );
}

// ============================================================================
// VOLUNTEER
// ============================================================================
function FoundationVolunteerForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", availability: "evenings", skills: "", interests: "" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/volunteers", {
        ...f,
        skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
        interests: f.interests.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Thank you for volunteering with the Foundation. We'll be in touch shortly.");
      setF({ name: "", email: "", phone: "", availability: "evenings", skills: "", interests: "" });
    } catch { toast.error("Could not submit. Please try again."); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="foundation-form-volunteer">
      <Field label="Name *" required value={f.name} onChange={(e) => set("name", e.target.value)} />
      <Field label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
      <Field label="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
      <Select label="Availability" value={f.availability} onChange={(e) => set("availability", e.target.value)} options={[
        { v: "weekdays", l: "Weekdays" }, { v: "evenings", l: "Evenings" }, { v: "weekends", l: "Weekends" }, { v: "flexible", l: "Flexible" },
      ]} />
      <Field label="Skills (comma-separated)" value={f.skills} onChange={(e) => set("skills", e.target.value)} />
      <Field label="Areas of Interest (comma-separated)" value={f.interests} onChange={(e) => set("interests", e.target.value)} />
      <div className="md:col-span-2">
        <button className="btn-primary" type="submit" data-testid="foundation-volunteer-submit">
          Sign Up to Volunteer <HelpingHand size={14} />
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// PARTNER INQUIRY  (uses /api/contact with topic=partner)
// ============================================================================
function FoundationPartnerForm() {
  const [f, setF] = useState({
    name: "", email: "", organization: "", phone: "",
    partner_type: "nonprofit", message: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", {
        name: f.name,
        email: f.email,
        phone: f.phone || null,
        topic: "partner",
        message: `[Foundation Partner Inquiry] Org: ${f.organization || "n/a"} · Type: ${f.partner_type}\n\n${f.message}`,
      });
      toast.success("Partner inquiry received. The Foundation team will respond within 3 business days.");
      setF({ name: "", email: "", organization: "", phone: "", partner_type: "nonprofit", message: "" });
    } catch { toast.error("Could not submit. Please try again."); }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="foundation-form-partner">
      <Field label="Name *" required value={f.name} onChange={(e) => set("name", e.target.value)} />
      <Field label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
      <Field label="Organization" value={f.organization} onChange={(e) => set("organization", e.target.value)} />
      <Field label="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
      <Select label="Partner Type" value={f.partner_type} onChange={(e) => set("partner_type", e.target.value)} options={[
        { v: "nonprofit", l: "Nonprofit / 501(c)(3)" },
        { v: "va", l: "VA Medical Center" },
        { v: "clinical", l: "Behavioral Health Provider" },
        { v: "workforce", l: "Workforce / Trade Union" },
        { v: "municipal", l: "City / County / Public Agency" },
        { v: "faith", l: "Faith-Based Organization" },
        { v: "foundation", l: "Foundation / Family Office" },
        { v: "other", l: "Other" },
      ]} />
      <TextArea label="Tell us how you'd like to collaborate *" required value={f.message} onChange={(e) => set("message", e.target.value)} />
      <div className="md:col-span-2">
        <button className="btn-primary" type="submit" data-testid="foundation-partner-submit">
          Submit Partner Inquiry <Handshake size={14} />
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// RESIDENT ASSISTANCE  (uses /api/housing/apply with emergency=false)
// ============================================================================
function FoundationAssistanceForm() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", veteran_status: "veteran",
    housing_type: "transitional", household_size: 1, monthly_income: "", notes: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/housing/apply", {
        ...f,
        household_size: Number(f.household_size),
        monthly_income: f.monthly_income ? Number(f.monthly_income) : null,
        emergency: false,
      });
      toast.success("Assistance request received. A Foundation counselor will reach out within 48 hours.");
      setF({ full_name: "", email: "", phone: "", veteran_status: "veteran", housing_type: "transitional", household_size: 1, monthly_income: "", notes: "" });
    } catch { toast.error("Could not submit. Please try again."); }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="foundation-form-assistance">
      <div className="md:col-span-2 border border-gold/30 bg-gold/5 p-4 text-sm text-white/80 flex items-start gap-3">
        <AlertTriangle className="text-gold flex-shrink-0 mt-0.5" size={18} />
        <span>
          For immediate danger, call 911. For the Veterans Crisis Line, dial 988 then press 1.
          Use this form for non-emergency assistance — housing, recovery, workforce, or family support.
        </span>
      </div>
      <Field label="Full Name *" required value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
      <Field label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
      <Field label="Phone *" required value={f.phone} onChange={(e) => set("phone", e.target.value)} />
      <Select label="Veteran Status" value={f.veteran_status} onChange={(e) => set("veteran_status", e.target.value)} options={[
        { v: "veteran", l: "Veteran" }, { v: "active_duty", l: "Active Duty" }, { v: "dependent", l: "Dependent" }, { v: "civilian", l: "Civilian" },
      ]} />
      <Select label="Assistance Type" value={f.housing_type} onChange={(e) => set("housing_type", e.target.value)} options={[
        { v: "transitional", l: "Transitional Housing" },
        { v: "recovery", l: "Recovery Housing" },
        { v: "affordable", l: "Affordable Housing" },
        { v: "workforce", l: "Workforce Support" },
      ]} />
      <Field label="Household Size" type="number" min="1" value={f.household_size} onChange={(e) => set("household_size", e.target.value)} />
      <Field label="Monthly Income (USD)" type="number" value={f.monthly_income} onChange={(e) => set("monthly_income", e.target.value)} />
      <TextArea label="Briefly describe your situation *" required value={f.notes} onChange={(e) => set("notes", e.target.value)} />
      <div className="md:col-span-2">
        <button className="btn-primary" type="submit" data-testid="foundation-assistance-submit">
          Submit Assistance Request <HomeIcon size={14} />
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// GRANT DOCUMENT UPLOAD (placeholder — informs user, captures inbound interest via /contact)
// ============================================================================
function FoundationGrantUpload() {
  const [f, setF] = useState({ name: "", email: "", organization: "", grant_program: "", notes: "" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const [filename, setFilename] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", {
        name: f.name,
        email: f.email,
        phone: null,
        topic: "grant",
        message: `[Foundation Grant Submission Intake] Org: ${f.organization || "n/a"} · Program: ${f.grant_program || "n/a"} · File: ${filename || "(not uploaded)"}\n\n${f.notes}`,
      });
      toast.success("Grant intake received. The Foundation team will reach out with a secure upload link.");
      setF({ name: "", email: "", organization: "", grant_program: "", notes: "" });
      setFilename("");
    } catch { toast.error("Could not submit. Please try again."); }
  };

  return (
    <div className="space-y-6" data-testid="foundation-grant-section">
      <div className="border border-gold/30 bg-gold/5 p-5 text-sm text-white/80 flex items-start gap-3">
        <FileText className="text-gold flex-shrink-0 mt-0.5" size={18} />
        <div>
          <div className="text-white font-medium mb-1">Secure document portal — coming soon</div>
          <div className="text-white/70">
            File upload will be enabled when the Foundation's secure document portal is provisioned.
            In the meantime, use the intake form below — we'll send you a secure upload link by reply.
          </div>
        </div>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="foundation-form-grant">
        <Field label="Name *" required value={f.name} onChange={(e) => set("name", e.target.value)} />
        <Field label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
        <Field label="Organization / Agency" value={f.organization} onChange={(e) => set("organization", e.target.value)} />
        <Field label="Grant Program Name" value={f.grant_program} onChange={(e) => set("grant_program", e.target.value)} />
        <label className="block md:col-span-2">
          <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Attach Document (placeholder)</span>
          <div className="flex items-center gap-3">
            <label className="btn-ghost text-sm cursor-pointer inline-flex" data-testid="foundation-grant-file-trigger">
              <Upload size={14} /> Choose File
              <input
                type="file"
                className="hidden"
                onChange={(e) => setFilename(e.target.files?.[0]?.name || "")}
                data-testid="foundation-grant-file-input"
              />
            </label>
            <span className="text-xs text-white/60" data-testid="foundation-grant-filename">
              {filename || "No file selected · upload is a placeholder for V1"}
            </span>
          </div>
        </label>
        <TextArea label="Notes for the Foundation team" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        <div className="md:col-span-2">
          <button className="btn-primary" type="submit" data-testid="foundation-grant-submit">
            Submit Grant Intake <FileText size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}
